import { customAlphabet } from 'nanoid';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import esriRequest from '@arcgis/core/request';
import { ISelect, setLoadingMessage, setShowGlobalLoading } from '../../../store/slices/appSlice';
import { UploadFileOptions } from '../RenderFileUpload/RenderFileUpload';
import { GeoJSONgeneric, IAnalysisCounts, OriginType, WORLD_COUNTRIES } from '../types';
import { mapControllerVerification } from '../../../controllers/MapControllerVerification';
import { queryFeatures } from '../../../services/esri';
import { NotificationType, showNotification } from '../../../utils/showNotifications';
import * as projection from '@arcgis/core/geometry/projection';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import Point from '@arcgis/core/geometry/Point';
import { SetStateAction } from 'react';
import * as XLSX from 'xlsx';
import { Method } from '../../../config/s3publicfiles.config';
import {
  setLatitude,
  setLongitude,
  setOriginUploadedFile,
  setSelectedCountry,
} from '../../../store/slices/verificationSlice';
import { requiredIcpMsHeaders, requiredSiraHeaders, requiredSiraXRFHeaders, requiredXrfHeaders } from '../../../config/fileHeaders.config';
import utilsController from '../../../controllers/UtilsController';
import {
  docDefinition,
  generatePDFPage1,
  generatePDFPage2,
  generatePDFPage3,
  generatePDFPage4,
  generatePDFPage5,
  generatePDFPage6,
} from '../../../config/pdfReport.config';
import { OperationType } from '../../../controllers/genusTableController';
import pdfMake from 'pdfmake/build/pdfmake';
import 'pdfmake/build/vfs_fonts';
import { Action, Dispatch } from '@reduxjs/toolkit';
import shp from 'shpjs';
import store from '../../../store/store';
import { CONFIG_URLS } from '../../../config/url.config';
import { setUserHasPermissions } from '../../../store/slices/authSlice';

export interface IAttributes {
  Country_Name: string;
  Sample_Count: number;
}

export interface IAttachmentForm {
  blob: Blob;
  fileName: string;
}

/*
 * Description: This function generates a random ID consisting of 3 uppercase letters followed by 3 digits.
 * return {string} - The generated random ID.
 * */
const generateRandomId = () => {
  // const letterGen = customAlphabet('ABCDEFGHIJKLMNOPQRSTUVWXYZ', 3);
  const numberGen = customAlphabet('0123456789', 6);

  // const id = letterGen() + numberGen();
  const id = numberGen();

  return id;
};

const stringifyNumber = (value: number) => {
  if (value < 10) return `0${value}`;
  return value.toString();
};

const generateDateReportNumber = () => {
  const getYear = new Date().getFullYear();
  const getMonth = new Date().getMonth() + 1;
  const getDay = new Date().getDate();

  const updatedMonth = stringifyNumber(getMonth);
  const updatedDay = stringifyNumber(getDay);
  return `${getYear}${updatedMonth}${updatedDay}`;
};

/*
 * Description: This function generates a report number in the format WFID_ID-YYYYMMDD-123456.
 * return {string} - The generated report number.
 * */
export const generateReportNumber = (fileDataHashMap?: any) => {
  const randomId = generateRandomId();
  const dateReportNumber = generateDateReportNumber();
  // Extract WFID ID from the uploaded template
  const wfid_sample_id = fileDataHashMap?.get('WFID ID') || '0000';

  return `${wfid_sample_id}-${dateReportNumber}-${randomId}`;
};

// Function to format the PDF table content
export const formatPDFTableContent = (attributes: IAttributes[], country: string) => {
  // Sort attributes alphabetically by Country_Name
  const sortedAttributes = [...attributes].sort((a, b) => 
    a.Country_Name.localeCompare(b.Country_Name)
  );
  
  const formattedData = sortedAttributes.map(({ Country_Name, Sample_Count }) => {
    if (Country_Name === country) {
      return [{ text: Country_Name, bold: true }, { text: Sample_Count, bold: true }];
    }

    return [Country_Name, { text: Sample_Count }];
  });
  return formattedData;
};

export const generateAttachmentForm = (params: IAttachmentForm) => {
  const { blob, fileName } = params;
  const formData = new FormData();
  formData.append('attachment', blob, fileName);
  formData.append('f', 'json');
  return formData;
};

// Helper function to format numbers with proper precision
// Uses scientific notation for very small values (< 0.01) and 2 decimal places for others
const formatNumber = (value: number): string => {
  if (Math.abs(value) < 0.01 && value !== 0) {
    return value.toExponential(2);
  }
  return value.toFixed(2);
};

// Helper function to format labels by replacing underscores with proper text
const formatLabel = (label: string): string => {
  return label
    .replace(/_lipidextracted/g, ' lipid extracted')
    .replace(/_cellulose/g, ' cellulose')
    .replace(/_nitrated/g, ' nitrated')
    .replace(/_/g, ' '); // Replace any remaining underscores with spaces
};

// Helper function to group and format related measurement values
const groupAndFormatMeasurements = (data: ISelect[]): ISelect[] => {
  if (!data?.length) return [];
  
  const groupedData: { [key: string]: { value: number | null; std: number | null; originalIndex: number } } = {};
  const otherData: { item: ISelect; originalIndex: number }[] = [];
  
  // First pass: identify all std fields and their base fields
  const stdFields = new Map<string, string>();
  data.forEach((item) => {
    const label = item.label;
    if (label.includes('_std_')) {
      const baseKey = label.replace('_std_', '_');
      stdFields.set(label, baseKey);
    } else if (label.includes('_std')) {
      const baseKey = label.replace('_std', '');
      stdFields.set(label, baseKey);
    }
  });
  
  // Second pass: group related measurements while preserving order
  data.forEach((item, index) => {
    const label = item.label;
    const value = parseFloat(item.value);
    
    // Skip if not a valid number
    if (isNaN(value)) {
      otherData.push({ item, originalIndex: index });
      return;
    }
    
    // Check if this is a standard deviation field
    if (stdFields.has(label)) {
      const baseKey = stdFields.get(label)!;
      if (!groupedData[baseKey]) {
        groupedData[baseKey] = { value: null, std: null, originalIndex: index };
      }
      groupedData[baseKey].std = value;
    } else {
      // Check if this field has a corresponding std field
      const hasStd = stdFields.has(label + '_std') || 
                    stdFields.has(label.replace(/([^_]+)$/, '_std_$1')) ||
                    Array.from(stdFields.values()).includes(label);
      
      if (hasStd) {
        if (!groupedData[label]) {
          groupedData[label] = { value: null, std: null, originalIndex: index };
        }
        groupedData[label].value = value;
      } else {
        // No corresponding std field, treat as standalone
        otherData.push({ item, originalIndex: index });
      }
    }
  });
  
  // Format grouped measurements and preserve order
  const formattedData: { item: ISelect; originalIndex: number }[] = [];
  
  Object.entries(groupedData).forEach(([label, data]) => {
    if (data.value !== null && data.std !== null) {
      // Both value and std exist, format as x±y
      // const formattedValue = `${formatNumber(data.value)} ± ${formatNumber(data.std)}`;
      const formattedValue = `${formatNumber(data.value)}`;
      formattedData.push({ 
        item: { label: formatLabel(label), value: formattedValue }, 
        originalIndex: data.originalIndex 
      });
    } else if (data.value !== null) {
      // Only value exists, format normally
      const formattedValue = formatNumber(data.value);
      formattedData.push({ 
        item: { label: formatLabel(label), value: formattedValue }, 
        originalIndex: data.originalIndex 
      });
    }
  });
  
  // Add other data with proper formatting
  otherData.forEach(({ item, originalIndex }) => {
    const value = parseFloat(item.value);
    if (!isNaN(value)) {
      formattedData.push({ 
        item: { label: formatLabel(item.label), value: formatNumber(value) }, 
        originalIndex 
      });
    } else {
      formattedData.push({ 
        item: { label: formatLabel(item.label), value: item.value }, 
        originalIndex 
      });
    }
  });
  
  // Sort by original index to preserve order
  formattedData.sort((a, b) => a.originalIndex - b.originalIndex);
  
  return formattedData.map(({ item }) => item);
};

/**
 * Formats test sample data for PDF display by:
 * 1. Grouping related measurements (e.g., d18O_lipidextracted and d18O_std_lipidextracted)
 * 2. Formatting them as "x±y" when both value and standard deviation exist
 * 3. Rounding numbers to 2 decimal places or using scientific notation for very small values
 * 4. Replacing underscores with proper labels (_lipidextracted → "lipid extracted", etc.)
 * 5. Preserving the original order of measurements
 */
export const concatObjValues = (data: ISelect[]) => {
  if (!data?.length) return [];
  
  // First group and format the measurements
  const formattedData = groupAndFormatMeasurements(data);
  
  return formattedData.map((item) => `${item.label}: ${item.value}`);
};

// Function to replace empty values with null in a row
export const replaceEmptyToNull = (row: any, headers: string[]) => {
  let result = [] as any;

  headers.forEach((_, index) => {
    if (row[index]) {
      result.push(row[index]);
    } else {
      result.push(null);
    }
  });

  return result;
};

// Function to merge headers and rows data into a Map object with header as key and row as value
// to easily access the data by header name
export const mergeHeadersRowsData = (rows: any, headers: string[]) => {
  const hashMap = new Map();

  headers.forEach((header, index) => {
    if (rows[index]) {
      hashMap.set(header, rows[index]);
    }
  });

  return hashMap;
};

/*
 *Description: This function splits the hashMap data into two arrays based on the size of the map.
 * We need to split the data into two arrays to display them in two columns in the PDF report.
 * It ignores the keys that are in the listToIgnore array.since they are not needed in the report.
 * return {firstHalf: [], secondHalf: [], result: []} - The split data.
 * */
export const splitHashMapData = (hashMap: Map<string, number | string | null>) => {
  const result = [] as { label: string; value: string | number | null }[];
  const listToIgnore = ['Order number', 'Lab', 'Date', 'Genus', 'Species (Genus + Species)', 'Remark'];
  for (const [key, value] of hashMap.entries()) {
    if (!listToIgnore.includes(key)) {
      result.push({ label: key, value });
    }
  }

  // we need to split result array into two arrays based on the size of the map
  const half = Math.ceil(result.length / 2);
  const firstHalf = result.slice(0, half);
  const secondHalf = result.slice(half);

  return { firstHalf, secondHalf };
};

export const parseUploadData = async (value: any, type: UploadFileOptions, dispatch: Dispatch<Action<any>>) => {
  switch (type) {
    case UploadFileOptions.GeoJSON:
      if (!value) return;
      try {
        const fileReader = new FileReader();
        const fileContent = await new Promise<string>((resolve, reject) => {
          fileReader.onload = (event) => resolve(event.target?.result as string);
          fileReader.onerror = (error) => reject(error);
          fileReader.readAsText(value);
        });

        dispatch(setOriginUploadedFile(JSON.parse(fileContent)));
        console.log(JSON.parse(fileContent));
      } catch (error) {
        console.error('Error uploading GeoJSON:', error);
      }
      break;
    case UploadFileOptions.Shapefile:
      console.log('shapefile');
      try {
        const data = await value.arrayBuffer();
        const geojson = await shp(data);
        console.log('shapefile', geojson);
        dispatch(setOriginUploadedFile(geojson));
      } catch (error) {
        console.error('Error uploading Shapefile:', error);
      }
      break;
  }
};

export const generatedFeatureServiceQuery = () => {
  const stats = {
    onStatisticField: 'probability',
    outStatisticFieldName: 'total',
    statisticType: 'count',
  };

  // anything less than 0.05 will be red color on the map
  const params = {
    where: 'probability < 0.05',
    outFields: ['*'],
    returnGeometry: false,
    returnDistinctValues: true,
    outStatistics: [stats],
  };

  return params;
};

export const getAnalysisCalculation = (redCount: IAnalysisCounts[], total: IAnalysisCounts[]) => {
  const redCountValue = redCount[0]?.total || 0;
  const totalCountValue = total[0]?.total || 0;

  const result = (redCountValue / totalCountValue) * 100;
  return Math.round(result);
};

export const getShapefileParams = (fileName: string) => {
  return {
    name: fileName,
    targetSR: mapControllerVerification.getspatialReference(),
    maxRecordCount: 1000,
    enforceInputFileSizeLimit: true,
    enforceOutputJsonSizeLimit: true,
    generalize: true,
    reducePrecision: true,
    numberOfDigitsAfterDecimal: 0,
    maxAllowableOffset: 10,
  };
};

export const getMyContentShapefile = (params: any) => {
  return {
    filetype: 'shapefile',
    publishParameters: JSON.stringify(params),
    f: 'json',
  };
};

export const findCountryFeature = async (countryName: string): Promise<any> => {
  const params = {
    where: `COUNTRY = '${countryName}'`,
    outFields: ['*'],
    returnGeometry: true,
  };
  const result = await queryFeatures(params, WORLD_COUNTRIES.url);
  if (!result.success) {
    showNotification({
      type: NotificationType.Error,
      message: 'Error finding country feature. Please refresh the page and try again',
      title: 'Error',
    });
    return null;
  }

  if (result?.data?.features?.length) {
    const countryFeature = result.data?.features[0];

    return countryFeature;
  }
  return null;
};

export const extractGenusData = async (setGenusData: React.Dispatch<SetStateAction<Set<string>>>) => {
  try {
    const query = {
      where: '1=1',
      outFields: ['Genus'],
      returnGeometry: false,
      returnDistinctValues: true,
    };

    const response = await queryFeatures(query, CONFIG_URLS.REFERENCE_PLATFORM);

    if (!response?.success) {
      // show Error message
      const message = response?.message as any;
      const errorType = message?.name;

      if (errorType === 'identity-manager:not-authorized') {
        store.dispatch(setUserHasPermissions(false));

        showNotification({
          type: NotificationType.Error,
          message: 'You are not authorized to access this resource. If you think this is a mistake, please contact support.',
          title: 'Error',
        });
      } else {
        showNotification({
          type: NotificationType.Error,
          message: 'Error extracting genus data. Please refresh the page and try again.',
          title: 'Error',
        });
      }

      return;
    }
    const extractedGenusData = new Set(response.data?.features.map((feature) => feature.attributes.Genus));
    setGenusData(extractedGenusData);
  } catch (error) {
    console.error('Error extracting genus data:', error);
  }
};

export const getBufferGeometry = (point: Point) => {
  const buffer = geometryEngine.geodesicBuffer(point, 10, 'kilometers');

  let singleBuffer;
  if (Array.isArray(buffer)) {
    singleBuffer = buffer[0];
  } else {
    singleBuffer = buffer;
  }

  return { bufferGeometry: singleBuffer };
};

////
export const handleSampleFileUpload = async (
  event: any,
  setFile: React.Dispatch<SetStateAction<File | null>>,
  setFileDataHashMap: React.Dispatch<SetStateAction<any>>,
  setTestSampleDataFromFile: React.Dispatch<SetStateAction<any>>,
  setSelectedGenus: React.Dispatch<SetStateAction<string>>,
  selectedMethod: Method,
  genusData: Set<string>
) => {
  const file = event;
  if (!file) return;
  setFile(file);
  if (file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target === null) {
        console.error('FileReader target is null');
        return;
      }
      const content = e.target.result;

      // Read the file as binary string
      const data = new Uint8Array(content as ArrayBuffer);
      const workbook = XLSX.read(data, { type: 'array' });
      // Assuming the first sheet is the one you want to read
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      // Extract headers
      const headers = jsonData[0] as string[];
      const row = jsonData[1] as string[];
      const updatedRowData = replaceEmptyToNull(row, headers);

      const hashMap = mergeHeadersRowsData(updatedRowData, headers);
      setFileDataHashMap(hashMap);

      const splittedData = splitHashMapData(hashMap);
      setTestSampleDataFromFile(splittedData);

      // Process headers and rows as needed
      let requiredHeaders: string[] = [];
      if (selectedMethod === Method.SIRA) {
        requiredHeaders = requiredSiraHeaders;
      } else if (selectedMethod === Method.XRF) {
        requiredHeaders = requiredXrfHeaders;
      } else if (selectedMethod === Method.ICPMS) {
        requiredHeaders = requiredIcpMsHeaders;
      } else if (selectedMethod === Method.SIRAXRF) {
        requiredHeaders = requiredSiraXRFHeaders;
      }

      const missingHeaders = requiredHeaders.filter((header) => !headers.includes(header));
      const extraHeaders = headers.filter((header) => !requiredHeaders.includes(header));

      if (missingHeaders.length > 0 || extraHeaders.length > 0) {
        if (missingHeaders.length > 0) {
          /* errorMessage +=
            "Missing headers: " + missingHeaders.join(", ") + ".\n"; */
        }
        if (extraHeaders.length > 0) {
          //errorMessage += "Extra headers: " + extraHeaders.join(", ") + ".\n";
        }
        //alert("Invalid Excel file.\n" + errorMessage);
        showNotification({
          type: NotificationType.Error,
          message: `Columns in the uploaded file do not match the selected analysis method: ${selectedMethod}. Please check you are using the latest template and try again.`,
          title: 'Error: Incorrect Chemical Data Template',
          autoClose: false,
        });
        return;
      }

      // Extract the genus value (assuming there's a "Genus" column)
      const genusIndex = headers.indexOf('Genus');
      if (genusIndex === -1) {
        showNotification({
          type: NotificationType.Error,
          message: "The genus column is missing from the uploaded file. Please check you are using the latest template and try again.",
          title: 'Error: Genus Not Found',
          autoClose: false,
        });
        return;
      }
      const genusValue = row[genusIndex];

      if (!genusData.has(genusValue)) {
        showNotification({
          type: NotificationType.Error,
          message: `Genus "${genusValue}" is not valid.`,
          title: 'Error: Invalid Genus',
          autoClose: false,
        });
        return;
      }
      showNotification({
        type: NotificationType.Success,
        message: 'The uploaded file is valid and has been processed.',
        title: 'Success',
        autoClose: true,
      });
      showNotification({
        type: NotificationType.Success,
        message: `Genus detected: ${genusValue}`,
        title: 'Success',
        autoClose: true,
      });

      const genusName = genusValue as string;
      mapControllerVerification.addGenusLayer(genusName);
      setSelectedGenus(genusName);
      mapControllerVerification.addGenusRangeFeatureLayer(genusName);
    };
    reader.readAsArrayBuffer(file);
  }
};

export const handleFileChange = (
  value: any,
  setShowPostResultInfo: React.Dispatch<SetStateAction<boolean>>,
  setPostResultInfoMessage: React.Dispatch<SetStateAction<string>>,
  setFile: React.Dispatch<SetStateAction<File | null>>,
  setFileDataHashMap: React.Dispatch<SetStateAction<any>>,
  setTestSampleDataFromFile: React.Dispatch<SetStateAction<any>>,
  setSelectedGenus: React.Dispatch<SetStateAction<string>>,
  selectedMethod: Method,
  genusData: Set<string>
): void => {
  setShowPostResultInfo(false);
  setPostResultInfoMessage('');
  mapControllerVerification.removeAllLayers();
  setFile(value);
  handleSampleFileUpload(
    value,
    setFile,
    setFileDataHashMap,
    setTestSampleDataFromFile,
    setSelectedGenus,
    selectedMethod,
    genusData
  );
};

export const handleSelectionMethod = (
  method: Method,
  setFile: React.Dispatch<SetStateAction<File | null>>,
  setSelectedMethod: React.Dispatch<SetStateAction<Method>>
) => {
  setFile(null);
  setSelectedMethod(method);
};

export const disableVerifyButton = (
  disclaimerAccepted: any,
  privacyPolicyAccepted: any,
  file: any,
  selectedOption: OriginType | null,
  latitude: number | string | undefined,
  longitude: number | string | undefined,
  selectedCountry: any,
  originUploadedFile: any,
  canUserRunAnalysis: boolean
) => {
  if (!canUserRunAnalysis) return true;
  if (!disclaimerAccepted || !privacyPolicyAccepted) return true;
  if (!file || !selectedOption) return true;
  if (selectedOption === OriginType.Coordinates) {
    if (!latitude || !longitude) return true;
  }
  if (selectedOption === OriginType.Country) {
    if (!selectedCountry) return true;
  }

  if (selectedOption === OriginType.Shapefile || selectedOption === OriginType.GeoJSON) {
    if (!originUploadedFile) return true;
  }
};

export const handleReset = (
  setPostResultInfoMessage: React.Dispatch<string>,
  setShowPostResultInfo: React.Dispatch<boolean>,
  setFile: React.Dispatch<SetStateAction<File | null>>,
  setSelectedOption: React.Dispatch<SetStateAction<OriginType | null>>,
  dispatch: Dispatch<Action<any>>
) => {
  mapControllerVerification.removeAllLayers();
  setFile(null);
  setSelectedOption(null);
  dispatch(setSelectedCountry(''));
  dispatch(setLatitude(''));
  dispatch(setLongitude(''));
  dispatch(setOriginUploadedFile(null));
  setPostResultInfoMessage('');
  setShowPostResultInfo(false);
};

export const overrideAttributes = (featureSet: __esri.FeatureSet | null, attributes: any) => {
  if (!featureSet?.features && !featureSet?.features?.length) return null;
  featureSet.features[0].attributes = attributes;
  return featureSet.features[0];
};

export const handlePrintReport = async (
  selectedGenus: string,
  generatedFeatureServiceUrl: string | null,
  selectedMethod: Method,
  testSampleDataFromFile: any,
  fileDataHashMap: any,
  currentAnalysisId: string,
  dispatch: Dispatch<Action<any>>,
  selectedOption: OriginType | null
) => {
  dispatch(setShowGlobalLoading(true));
  dispatch(setLoadingMessage('Generating Report...'));
  const { speciesData } = store.getState().appSlice;
  const { selectedCountry, latitude, longitude, originUploadedFile } = store.getState().verificationSlice;
  const findByLabel = speciesData.find((option) => option.label === selectedGenus);
  const mapImageUrl = await mapControllerVerification.geMapScreenshot();
  if (findByLabel) {
    const response = await queryFeatures(
      {
        where: '1=1',
        outFields: ['Country_Name', 'Sample_Count'],
        returnGeometry: false,
      },
      `${findByLabel.value}/0`
    );
    const attributes = utilsController.getAttributes(response.data);

    const formattedData = formatPDFTableContent(attributes, selectedCountry || '');

    const params = generatedFeatureServiceQuery();
    const redValueCount = await queryFeatures(params, generatedFeatureServiceUrl || '');
    const totalCount = await queryFeatures({ ...params, where: '1=1' }, generatedFeatureServiceUrl || '');

    const redCountAttributes = utilsController.getAttributes(redValueCount.data);
    const totalCountAttributes = utilsController.getAttributes(totalCount.data);

    const analysisResult = getAnalysisCalculation(redCountAttributes, totalCountAttributes);

    const reportNumber = generateReportNumber(fileDataHashMap);
    
    // Generate claimedOrigin based on selectedOption
    const claimedOrigin = await generateClaimedOrigin(
      selectedOption,
      selectedCountry,
      latitude,
      longitude,
      originUploadedFile
    );
    
    const pdfPage1 = generatePDFPage1({
      reportNumber,
      totalPixels: analysisResult,
      claimedOrigin,
      selectedCountry,
      selectedGenus,
      commonName: `${findByLabel.commonName}`,
      selectedMethod,
      firstColumn: concatObjValues(testSampleDataFromFile.firstHalf),
      secondColumn: concatObjValues(testSampleDataFromFile.secondHalf),
      lab: fileDataHashMap.get('Lab') || '',
      measurementLabId: fileDataHashMap.get('Measurement Laboratory ID') || 'Not provided',
      speciesVerifiedBy: fileDataHashMap.get('Species Verified By') || 'Not verified',
    });
    const pdfPage2 = generatePDFPage2({
      selectedGenus,
      tableContent: formattedData,
    });

    const pdfPage3 = generatePDFPage3(findByLabel.reportText1);
    const pdfPage4 = generatePDFPage4({ mapImage: mapImageUrl });
    const pdfPage5 = generatePDFPage5();
    const pdfPage6 = generatePDFPage6();

    const getDefaultDefinition = await docDefinition();

    const pdfDocDefinition = {
      ...getDefaultDefinition,
      content: [...pdfPage1, ...pdfPage2, ...pdfPage3, ...pdfPage4, ...pdfPage5, ...pdfPage6],
    } as any;

    const queryClause = `UniqueID = '${currentAnalysisId}'`;
    const getRecordResponse = await mapControllerVerification.getCreatedLogRecord(queryClause);
    const getAttributesById = utilsController.getAttributes(getRecordResponse.data);
    const objData = getAttributesById[0];
    objData.ReportID = reportNumber;
    // certificationStatus is now set during initial log record creation, so no need to set it here

    // overwrite attributes from feature set
    const updatedGraphic = overrideAttributes(getRecordResponse.data, objData);
    await mapControllerVerification.createReportLogRecord({ logAttributes: objData, type: OperationType.Update });
    const reportName = `WFID-Evaluation-${reportNumber}.pdf`;
    pdfMake.createPdf(pdfDocDefinition).download(reportName);
    pdfMake.createPdf(pdfDocDefinition).getBlob(async (blob) => {
      if (blob) {
        const formData = generateAttachmentForm({ blob, fileName: reportName });
        const logRecordLayer = mapControllerVerification.getLogReportLayer();

        if (!updatedGraphic) return;
        await logRecordLayer?.addAttachment(updatedGraphic, formData);

        dispatch(setShowGlobalLoading(false));
        dispatch(setLoadingMessage(''));
      }
    });
  }
};

const isValidLatitude = (lat: number) => {
  return typeof lat === 'number' && isFinite(lat) && lat >= -90 && lat <= 90;
};

const isValidLongitude = (lon: number) => {
  return typeof lon === 'number' && isFinite(lon) && lon >= -180 && lon <= 180;
};

// Function to check if the coordinates inputs are valid
export const isValidCoordinate = (lat: number, lon: number) => {
  return isValidLatitude(lat) && isValidLongitude(lon);
};

export const testIsWithin = (countryFeature: any, getDistributionData: any, dispatch: Dispatch<Action<any>>): any => {
  const isWithin = mapControllerVerification.isCountryWithinDistribution(countryFeature, getDistributionData);
  if (!isWithin) {
    showNotification({
      type: NotificationType.Error,
      title: 'Error: Claimed Location Not Available',
      message: "The claimed location of harvest is outside the region currently available for evaluation in the platform. Please contact support to discuss this case.",
    });
    dispatch(setShowGlobalLoading(false));
  }
  return isWithin;
};

export const generateGeoJSONurl = (geoJSON: GeoJSONgeneric) => {
  const geoJSONstr = JSON.stringify(geoJSON, null, 2);
  const blob = new Blob([geoJSONstr], { type: 'application/json' });
  return URL.createObjectURL(blob);
};

export const getPolygonData = (selectedOption: string, countryFeature: any) => {
  if (selectedOption === 'Country') return null;

  try {
    const feature = countryFeature?.geometry;
    
    if (!feature) {
      console.error('No geometry found in countryFeature:', countryFeature);
      return null;
    }

    // Ensure the geometry has a spatial reference
    if (!feature.spatialReference) {
      feature.spatialReference = { wkid: 4326 }; // Default to WGS84
    }

    const projectedFeature = projection.project(feature, new SpatialReference({ wkid: 102100 }));
    
    // Handle case where projection returns an array of geometries
    let geometryToProcess: any = projectedFeature;
    if (Array.isArray(projectedFeature)) {
      console.log('Projection returned multiple geometries, finding the largest one...');
      
      // Find the geometry with the largest area
      let largestGeometry = projectedFeature[0];
      let largestArea = 0;
      
      for (const geometry of projectedFeature) {
        try {
          // Only calculate area for polygons, skip other geometry types
          if (geometry.type === 'polygon') {
            const area = geometryEngine.planarArea(geometry);
            if (area > largestArea) {
              largestArea = area;
              largestGeometry = geometry;
            }
          } else {
            console.log('Skipping non-polygon geometry:', geometry.type);
          }
        } catch (error) {
          console.warn('Could not calculate area for geometry:', error);
        }
      }
      
      geometryToProcess = largestGeometry;
      console.log(`Selected geometry with area: ${largestArea.toFixed(2)} square units`);
    }
    
    // Debug: Check the size of the projected feature
    const featureSize = JSON.stringify(geometryToProcess).length;
    console.log('Projected feature size (bytes):', featureSize);
    
    // If the feature is too large, simplify it
    if (featureSize > 100000) { // 100KB limit for API
      console.warn('Projected feature is very large, simplifying geometry...');
      
      // Generalize the geometry to reduce size (reduce vertex count)
      // Handle case where generalize might return an array
      const generalizedResult = geometryEngine.generalize(geometryToProcess, 100); // 100 meter tolerance
      const simplifiedFeature = Array.isArray(generalizedResult) ? generalizedResult[0] : generalizedResult;
      
      const simplifiedSize = JSON.stringify(simplifiedFeature).length;
      console.log('Simplified feature size (bytes):', simplifiedSize);
      console.log('Size reduction:', Math.round((1 - simplifiedSize / featureSize) * 100) + '%');
      
      return simplifiedFeature;
    }
    
    return geometryToProcess;
  } catch (error) {
    console.error('Error in getPolygonData:', error);
    console.error('countryFeature:', countryFeature);
    return null;
  }
};

// Function to update the layer URL to point to the admin endpoint
export const updateLayerUrl = (url: string) => {
  const splitUrl = url.split('rest');
  splitUrl.splice(1, 0, 'rest/admin');
  const joinUrl = splitUrl.join(',');
  const removeCommas = joinUrl.replace(/,/g, '');
  return removeCommas;
};

// function to update definition of a feature service to disable editing at the field level
export const updateDefinition = async (params: any) => {
  const { token, definitionParams, defUrl } = params;

  const form = new FormData();
  form.append('f', 'json');
  form.append('token', token);
  form.append('async', 'true');
  form.append('updateDefinition', JSON.stringify(definitionParams));
  form.append('_ts', Date.now().toString());

  const options = {
    query: {
      f: 'pjson',
    },
    responseType: 'json',
    method: 'POST',
    body: form,
  } as any;

  try {
    const response = await esriRequest(defUrl, options);
    return {
      success: true,
      data: response,
    };
  } catch (err) {
    console.error('error', err);
    return {
      success: false,
      data: err,
    };
  }
};

// Function to find which country a polygon is within
const findCountryWithin = async (polygonGeometry: any): Promise<string> => {
  try {
    const results = await queryFeatures({
      where: '1=1',
      outFields: ['Name'],
      returnGeometry: true
    }, WORLD_COUNTRIES.url);

    if (!results.success || !results.data?.features) return 'Unknown Country';

    const intersectingCountries: string[] = [];

    for (const feature of results.data.features) {
      if (feature.geometry && geometryEngine.intersects(feature.geometry, polygonGeometry)) {
        intersectingCountries.push(feature.attributes.Name || 'Unknown Country');
      }
    }

    // Return NA if multiple countries, single country name if one, or Unknown Country if none
    if (intersectingCountries.length === 0) return 'Unknown Country';
    if (intersectingCountries.length === 1) return intersectingCountries[0];
    return 'NA';
  } catch (error) {
    console.error('Error finding country within:', error);
    return 'Unknown Country';
  }
};

export const generateClaimedOrigin = async (
  selectedOption: OriginType | null,
  selectedCountry: string | null,
  latitude: number | string | undefined,
  longitude: number | string | undefined,
  originUploadedFile: GeoJSONgeneric | null
): Promise<string> => {
  if (!selectedOption) {
    return 'NA';
  }

  switch (selectedOption) {
    case OriginType.Country:
      return selectedCountry || 'NA';
    
    case OriginType.Coordinates:
      if (latitude && longitude) {
        return typeof latitude === 'number' && typeof longitude === 'number' 
          ? `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
          : `${latitude}, ${longitude}`;
      }
      return 'NA';
    
    case OriginType.Shapefile:
    case OriginType.GeoJSON:
      if (originUploadedFile && originUploadedFile.features.length > 0) {
        const firstFeature = originUploadedFile.features[0];
        if (firstFeature.geometry && ['Polygon', 'MultiPolygon'].includes(firstFeature.geometry.type)) {
          try {
            const countryWithin = await findCountryWithin(firstFeature.geometry);
            return countryWithin !== 'Unknown Country' ? `${countryWithin} (sub-national)` : 'NA';
          } catch (error) {
            console.warn('Error finding country within:', error);
            return 'NA';
          }
        }
      }
      return 'NA';
    
    default:
      return 'NA';
  }
};
